import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { BarChart } from '@mui/x-charts/BarChart';
import { getAnalyticsTimeseries, exportAnalyticsTimeseriesCsv } from '../../api/runs';
import { ApiError } from '../../api/client';
import type {
  AnalyticsBucketGranularity,
  AnalyticsMetricKey,
  AnalyticsTimeseries,
} from '../../api/types';
import ErrorAlert from '../../components/ErrorAlert';
import LoadingSpinner from '../../components/LoadingSpinner';
import ChartSection from './ChartSection';

// Distinct hues for the stacked series. The first five are the default-on set
// (Runs, Participants, Submissions, Scored, Unscored); the rest are opt-in.
const METRICS: { key: AnalyticsMetricKey; defaultOn: boolean; color: string }[] = [
  { key: 'run_count', defaultOn: true, color: '#2850A9' },
  { key: 'participant_count', defaultOn: true, color: '#2E8FA9' },
  { key: 'submission_count', defaultOn: true, color: '#C0562B' },
  { key: 'scored_count', defaultOn: true, color: '#3E8E3E' },
  { key: 'unscored_count', defaultOn: true, color: '#8A8D91' },
  { key: 'team_count', defaultOn: false, color: '#7A4FBF' },
  { key: 'guest_count', defaultOn: false, color: '#8A5A2B' },
  { key: 'registered_count', defaultOn: false, color: '#B23A6F' },
];

const isoDate = (d: Date): string => d.toISOString().slice(0, 10);

interface AnalyticsRangeViewProps {
  orgSlug: string;
}

const AnalyticsRangeView: React.FC<AnalyticsRangeViewProps> = ({ orgSlug }) => {
  const { t } = useTranslation('runs');

  const today = useMemo(() => new Date(), []);
  const [dateFrom, setDateFrom] = useState(() => isoDate(new Date(today.getTime() - 30 * 86_400_000)));
  const [dateTo, setDateTo] = useState(() => isoDate(today));
  const [bucket, setBucket] = useState<AnalyticsBucketGranularity>('day');
  const [enabled, setEnabled] = useState<Set<AnalyticsMetricKey>>(
    () => new Set(METRICS.filter((m) => m.defaultOn).map((m) => m.key)),
  );

  const [data, setData] = useState<AnalyticsTimeseries | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!orgSlug) return;
    setLoading(true);
    setError(null);
    try {
      setData(await getAnalyticsTimeseries(orgSlug, { dateFrom, dateTo, bucket }));
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [orgSlug, dateFrom, dateTo, bucket]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleMetric = (key: AnalyticsMetricKey) =>
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const enabledMetrics = METRICS.filter((m) => enabled.has(m.key));
  const dataset = (data?.buckets ?? []).map((b) => ({ ...b, label: b.bucket_start.slice(0, 10) }));

  const handleExport = async () => {
    setExportError(null);
    try {
      const csv = await exportAnalyticsTimeseriesCsv(orgSlug, { dateFrom, dateTo, bucket });
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics-timeseries-${orgSlug}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err instanceof ApiError ? err.detail : String(err));
    }
  };

  const totals = data?.totals;

  return (
    <Box>
      {/* Range + bucket controls */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              type="date"
              label={t('analytics.range.from', { defaultValue: 'Von' })}
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              size="small"
            />
            <TextField
              type="date"
              label={t('analytics.range.to', { defaultValue: 'Bis' })}
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              size="small"
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="bucket-label">{t('analytics.range.bucket', { defaultValue: 'Gruppierung' })}</InputLabel>
              <Select
                labelId="bucket-label"
                label={t('analytics.range.bucket', { defaultValue: 'Gruppierung' })}
                value={bucket}
                onChange={(e) => setBucket(e.target.value as AnalyticsBucketGranularity)}
              >
                <MenuItem value="day">{t('analytics.range.bucketDay', { defaultValue: 'Tag' })}</MenuItem>
                <MenuItem value="week">{t('analytics.range.bucketWeek', { defaultValue: 'Woche' })}</MenuItem>
                <MenuItem value="month">{t('analytics.range.bucketMonth', { defaultValue: 'Monat' })}</MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => void handleExport()}>
              {t('analytics.range.exportCsv', { defaultValue: 'CSV exportieren' })}
            </Button>
          </Stack>
          {exportError && (
            <Box sx={{ mt: 2 }}>
              <ErrorAlert message={exportError} />
            </Box>
          )}
        </CardContent>
      </Card>

      {error && (
        <Box sx={{ mb: 2 }}>
          <ErrorAlert message={error} />
        </Box>
      )}

      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {/* Range-total tiles */}
          {totals && (
            <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', mb: 2 }}>
              {(['run_count', 'participant_count', 'submission_count', 'scored_count'] as AnalyticsMetricKey[]).map(
                (k) => (
                  <Card key={k} sx={{ flex: '1 1 180px', minWidth: 160 }}>
                    <CardContent>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {t(`analytics.range.metric.${k}`, { defaultValue: k })}
                      </Typography>
                      <Typography variant="h5" component="p" data-testid={`total-${k}`} sx={{ fontWeight: 700 }}>
                        {totals[k]}
                      </Typography>
                    </CardContent>
                  </Card>
                ),
              )}
            </Stack>
          )}

          {/* Metric toggles */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" component="p" sx={{ mb: 1 }}>
                {t('analytics.range.metricsLabel', { defaultValue: 'Metriken' })}
              </Typography>
              <FormGroup row>
                {METRICS.map((m) => (
                  <FormControlLabel
                    key={m.key}
                    control={<Checkbox checked={enabled.has(m.key)} onChange={() => toggleMetric(m.key)} size="small" />}
                    label={t(`analytics.range.metric.${m.key}`, { defaultValue: m.key })}
                  />
                ))}
              </FormGroup>
            </CardContent>
          </Card>

          {/* Stacked bar chart */}
          <ChartSection
            title={t('analytics.range.chartTitle', { defaultValue: 'Kennzahlen pro Zeitraum' })}
            toggleLabel={t('analytics.range.toggleTable', { defaultValue: 'Datentabelle anzeigen' })}
            empty={dataset.length === 0 || enabledMetrics.length === 0}
            emptyMessage={
              enabledMetrics.length === 0
                ? t('analytics.range.selectMetric', { defaultValue: 'Bitte mindestens eine Metrik auswählen.' })
                : t('analytics.range.empty', { defaultValue: 'Keine Läufe im gewählten Zeitraum.' })
            }
            chart={
              <BarChart
                dataset={dataset}
                xAxis={[{ dataKey: 'label', scaleType: 'band' }]}
                series={enabledMetrics.map((m) => ({
                  dataKey: m.key,
                  label: t(`analytics.range.metric.${m.key}`, { defaultValue: m.key }),
                  stack: 'total',
                  color: m.color,
                }))}
                height={340}
              />
            }
            table={
              <TableContainer>
                <Table size="small" aria-label={t('analytics.range.chartTitle', { defaultValue: 'Kennzahlen pro Zeitraum' })}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('analytics.range.date', { defaultValue: 'Datum' })}</TableCell>
                      {enabledMetrics.map((m) => (
                        <TableCell key={m.key} align="right">
                          {t(`analytics.range.metric.${m.key}`, { defaultValue: m.key })}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dataset.map((b) => (
                      <TableRow key={b.label}>
                        <TableCell>{b.label}</TableCell>
                        {enabledMetrics.map((m) => (
                          <TableCell key={m.key} align="right">
                            {b[m.key]}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            }
          />
        </>
      )}
    </Box>
  );
};

export default AnalyticsRangeView;
